import t from 'react-translate';
import { AskNovoAiStreamRequestPayload } from 'redux/actions/help-options';

export interface AskNovoAiStreamResultPayload {
  qnaId?: number;
  streamId?: string;
  questionText: string;
  answer: string;
  responseCode: number;
  responseType?: string;
  references?: AskNovoAiStreamReference[];
  certificateUrl?: string;
  externalReferences?: Array<{ title: string; url: string }>;
  liveEvents?: AskNovoAiStreamLiveEvent[];
  persistenceState?: string;
}

export interface AskNovoAiStreamReference {
  lecture_page_id?: number;
  component_id?: number;
  live_session_id?: number;
  title?: string;
  start_time?: string;
  end_time?: string;
  certificate_url?: string;
  resource_id?: number;
  url?: string;
}

export interface AskNovoAiStreamLiveEvent {
  id: number;
  lecturePageId: number;
  title?: string;
  startTime?: string;
  endTime?: string;
}

export type StatusEventData = {
  phase: string;
  message: string;
  elapsedMs?: number;
  routeClass?: string;
  targets?: string[];
  fallbackFrom?: string;
  responseLanguage?: string;
};

type DeltaEventData = {
  content: string;
};

type DoneEventData = {
  qnaId?: number;
  streamId?: string;
  persistenceState?: string;
  totalElapsedMs?: number;
  stageTimings?: Record<string, number>;
};

type ErrorEventData = {
  error: string;
};

type StreamParams = {
  aiApiEndpoint: string;
  token: string;
  streamRequest: AskNovoAiStreamRequestPayload;
  onStatus?: (status: StatusEventData) => void;
  onDelta?: (chunk: string) => void;
  onResult?: (result: AskNovoAiStreamResultPayload) => void;
  onDone?: (result: DoneEventData) => void;
  onError?: (err: Error) => void;
  abortSignal?: AbortSignal;
};

type SnakeCaseAskNovoAiStreamRequestPayload = {
  stream_id?: AskNovoAiStreamRequestPayload['streamId'];
  qna_id?: AskNovoAiStreamRequestPayload['qnaId'];
  course_id?: AskNovoAiStreamRequestPayload['courseId'];
  user_id?: AskNovoAiStreamRequestPayload['userId'];
  user_query?: AskNovoAiStreamRequestPayload['userQuery'];
  lecture_page_ids?: AskNovoAiStreamRequestPayload['lecturePageIds'];
  file_ids?: AskNovoAiStreamRequestPayload['fileIds'];
  action_base_url?: AskNovoAiStreamRequestPayload['actionBaseUrl'];
  persistence_callback_url?: AskNovoAiStreamRequestPayload['persistenceCallbackUrl'];
  data_env?: AskNovoAiStreamRequestPayload['dataEnv'];
  response_language?: AskNovoAiStreamRequestPayload['responseLanguage'];
};

type NormalizedAskNovoAiStreamRequestPayload =
  AskNovoAiStreamRequestPayload & SnakeCaseAskNovoAiStreamRequestPayload;

enum SSEEventType {
  Status = 'status',
  Delta = 'delta',
  Result = 'result',
  Done = 'done',
  Error = 'error',
}

const streamAskNovoAiResponse = async ({
  aiApiEndpoint,
  token,
  streamRequest,
  onStatus,
  onDelta,
  onResult,
  onDone,
  onError,
  abortSignal,
}: StreamParams) => {
  if (!aiApiEndpoint) {
    const error = new Error(
      t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.ERROR_DEFAULT_MESSAGE(),
    );
    onError?.(error);
    if (onError) return;
    throw error;
  }

  try {
    const rawStreamRequest = streamRequest as NormalizedAskNovoAiStreamRequestPayload;
    const normalizedPayload = {
      stream_id: rawStreamRequest.streamId ?? rawStreamRequest.stream_id,
      qna_id: rawStreamRequest.qnaId ?? rawStreamRequest.qna_id,
      course_id: rawStreamRequest.courseId ?? rawStreamRequest.course_id,
      user_id: rawStreamRequest.userId ?? rawStreamRequest.user_id,
      user_query: rawStreamRequest.userQuery ?? rawStreamRequest.user_query,
      lecture_page_ids: rawStreamRequest.lecturePageIds ?? rawStreamRequest.lecture_page_ids,
      file_ids: rawStreamRequest.fileIds ?? rawStreamRequest.file_ids,
      action_base_url: rawStreamRequest.actionBaseUrl ?? rawStreamRequest.action_base_url,
      persistence_callback_url: rawStreamRequest.persistenceCallbackUrl ?? rawStreamRequest.persistence_callback_url,
      data_env: rawStreamRequest.dataEnv ?? rawStreamRequest.data_env,
      response_language: rawStreamRequest.responseLanguage ?? rawStreamRequest.response_language,
    };

    const response = await fetch(`${aiApiEndpoint.replace(/\/+$/, '')}/courses/qna-bot/stream`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(normalizedPayload),
      signal: abortSignal,
    });

    if (!response.ok || !response.body) {
      throw new Error(
        t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.NETWORK_ERROR_MESSAGE(),
      );
    }

    const reader = response.body
      .pipeThrough(new TextDecoderStream())
      .getReader();

    let buffer = '';
    let currentEventType: SSEEventType | null = null;

    const processSSEEvent = (eventType: SSEEventType, data: string): boolean => {
      try {
        switch (eventType) {
          case SSEEventType.Status:
            onStatus?.(JSON.parse(data));
            return false;
          case SSEEventType.Delta: {
            const deltaData: DeltaEventData = JSON.parse(data);
            onDelta?.(deltaData.content);
            return false;
          }
          case SSEEventType.Result:
            onResult?.(JSON.parse(data));
            return false;
          case SSEEventType.Done:
            onDone?.(JSON.parse(data));
            return false;
          case SSEEventType.Error: {
            const errorData: ErrorEventData = JSON.parse(data);
            onError?.(new Error(errorData.error));
            return true;
          }
          default:
            return false;
        }
      } catch (err) {
        onError?.(err as Error);
        return true;
      }
    };

    const processLines = (lines: string[]): boolean => {
      let shouldCancel = false;

      lines.forEach((rawLine) => {
        if (shouldCancel) return;

        const line = rawLine.trim();
        if (!line || line.startsWith(':')) return;

        if (line.startsWith('event:')) {
          currentEventType = line.replace(/^event:\s*/, '') as SSEEventType;
        } else if (line.startsWith('data:')) {
          const data = line.replace(/^data:\s*/, '');
          if (currentEventType) {
            shouldCancel = processSSEEvent(currentEventType, data);
            currentEventType = null;
          }
        }
      });

      return shouldCancel;
    };

    const readNextChunk = (): Promise<void> => reader.read().then(({ value, done }) => {
      if (done) return Promise.resolve();

      buffer += value;
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      const shouldCancel = processLines(lines);
      if (shouldCancel) {
        return reader.cancel();
      }

      return readNextChunk();
    });

    await readNextChunk();
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      return;
    }
    const error = err as Error;
    onError?.(error);
    if (onError) return;
    throw error;
  }
};

export default streamAskNovoAiResponse;
